import getUserInfo from "./user-auth/UserInfo.js";
import { debounce } from "lodash";
import getLangflowHistory from "./getLangflowHistory";
import getAcordosHistory from "./getAcordosHistory";

/**
 * Fetches chat history from the main API
 * @param {number} page - Page number for pagination
 * @returns {Promise<Object>} - Promise resolving to transformed chat data
 */
const fetchHistoric = (page = 1) => {
  const userInfo = getUserInfo();

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Accept", "application/json");
  headers.append(
    "Authorization",
    `Basic ${process.env.REACT_APP_HISTORIC_KEY}`
  );

  const requestOptions = {
    method: "GET",
    headers: headers,
    redirect: "follow",
  };

  return fetch(
    `${process.env.REACT_APP_HISTORIC_API_URL}?page=${page}&user__email=${userInfo.email}`,
    requestOptions
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      const transformedData = {
        history: data.results.map((item) => ({
          protocol: item.protocol,
          summary: item.summary,
          pinned: item.pinned,
          historic: item.historic,
          knowledge: item.knowledge,
          type: item.type,
          messages: item.message_chat.map((message) => ({
            id: message.message_id,
            date: message.date,
            role: message.role,
            content: message.content,
            feedback: message.feedback,
          })),
        })),
      };
      return transformedData;
    })
    .catch((error) => {
      throw error;
    });
};

const debouncedFetchHistoric = debounce((resolve, reject, page) => {
  
  // Try to fetch main historic, but don't fail if it has CORS issues
  fetchHistoric(page)
    .catch((error) => {
      // Return empty history structure instead of failing completely
      return { history: [] };
    })
    .then(async (historicData) => {
      try {
        
        // Check if user is authenticated
        const userInfo = getUserInfo();
        if (!userInfo) {
          resolve(historicData);
          return;
        }

        const userEmail = userInfo.email;

        // Create a map of existing chats by protocol for easy lookup
        const existingChatsMap = new Map();
        (historicData.history || []).forEach((chat) => {
          existingChatsMap.set(chat.protocol, chat);
        });

        // Check DAGSol whitelist
        const dagsolWhitelistEnv =
          process.env.REACT_APP_DAGSOL_WHITELIST || "";
        const dagsolWhitelist = dagsolWhitelistEnv
          .split(",")
          .map((email) => email.trim().toLowerCase());

        const isDagsolWhitelisted = dagsolWhitelist.includes(
          userEmail.toLowerCase()
        );


        // Fetch and merge DAGSol history if whitelisted
        if (isDagsolWhitelisted) {
          try {
            const langflowChats = await getLangflowHistory(userEmail);

            if (langflowChats && langflowChats.length > 0) {
              // Add DAGSol chats that don't already exist
              langflowChats.forEach((newChat) => {
                if (!existingChatsMap.has(newChat.protocol)) {
                  existingChatsMap.set(newChat.protocol, newChat);
                }
              });
            }
          } catch (error) {
          }
        }

        // Check Acordos whitelist
        const acordosWhitelistEnv =
          process.env.REACT_APP_ACORDOS_WHITELIST || "";
        
        const acordosWhitelist = acordosWhitelistEnv
          .split(",")
          .map((email) => email.trim().toLowerCase());


        const isAcordosWhitelisted = acordosWhitelist.includes(
          userEmail.toLowerCase()
        );


        // Fetch and merge Acordos history if whitelisted
        if (isAcordosWhitelisted) {
          try {
            const acordosChats = await getAcordosHistory(userEmail);

            if (acordosChats && acordosChats.length > 0) {
              // Add Acordos chats that don't already exist
              acordosChats.forEach((newChat) => {
                if (!existingChatsMap.has(newChat.protocol)) {
                  existingChatsMap.set(newChat.protocol, newChat);
                }
              });
            } else {
            }
          } catch (error) {
          }
        } else {
        }

        // Convert back to array
        const mergedHistory = Array.from(existingChatsMap.values());

        // Sort to show special chats first: DAGSol, then Acordos, then regular chats
        mergedHistory.sort((a, b) => {
          const typeOrder = { dagsol: 1, acordos: 2, default: 3 };
          const orderA = typeOrder[a.type] || 3;
          const orderB = typeOrder[b.type] || 3;

          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return 0;
        });

        // Return merged data
        resolve({
          ...historicData,
          history: mergedHistory,
        });
      } catch (error) {
        resolve(historicData); // Still return the historic data on error
      }
    })
    .catch(reject);
}, 300);

/**
 * Fetches chat history and combines with multiple sources when appropriate:
 * - Main API: All users
 * - Langflow (DAGSol): Users in REACT_APP_DAGSOL_WHITELIST
 * - Acordos e Contratos: Users in REACT_APP_ACORDOS_WHITELIST
 *
 * @param {number} page - Page number for pagination (default: 1)
 * @returns {Promise<Object>} - Promise resolving to combined chat data with merged histories
 */
export const getHistoric = (page = 1) => {
  return new Promise((resolve, reject) => {
    debouncedFetchHistoric(resolve, reject, page);
  });
};